import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

import {
  addDaysYMD,
  fmtDMY,
  fmtYMD,
  ymdToDate,
} from "../../../../shared/utils/dateFormat";
import { getCalendarGrid } from "../../../../shared/utils/calendarUtils";
import {
  createTreasuryPeriod,
  listTreasuryOccupiedRanges,
} from "../api/cierresTesoreriaApi";

const CURRENT_CLOSURE_KEY = "sabi_tesoreria_cierre_actual";

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 4.5,
        py: 1.1,
        bgcolor: "secondary.200",
        borderRadius: "8px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function clampRange(start, end) {
  if (!start || !end) return { start, end };
  if (start <= end) return { start, end };
  return { start: end, end: start };
}

function isBetween(ymd, start, end) {
  if (!ymd || !start || !end) return false;
  return ymd >= start && ymd <= end;
}

function rangesOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA <= endB && endA >= startB;
}

function rangeContainsOccupied(start, end, occupiedRanges) {
  if (!start || !end) return false;
  return occupiedRanges.some((range) =>
    rangesOverlap(start, end, range.periodStart, range.periodEnd)
  );
}

function getOccupiedDay(ymd, occupiedRanges) {
  return occupiedRanges.find((range) => isBetween(ymd, range.periodStart, range.periodEnd));
}

function formatClosureName(start, end) {
  return `Cierre ${fmtDMY(ymdToDate(start))} - ${fmtDMY(ymdToDate(end))}`;
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

function getNextAvailableStart(occupiedRanges) {
  if (!occupiedRanges.length) return fmtYMD(new Date());

  const latestEnd = occupiedRanges.reduce((latest, range) => {
    if (!latest) return range.periodEnd;
    return range.periodEnd > latest ? range.periodEnd : latest;
  }, "");

  return addDaysYMD(latestEnd, 1);
}

function getMonthRangeFrom(startYmd) {
  const startDate = ymdToDate(startYmd) || new Date();
  const monthOffset = startDate.getDate() === 1 ? 0 : 1;
  const start = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
  const adjustedEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return { start: fmtYMD(start), end: fmtYMD(adjustedEnd) };
}

function CalendarRangeTwoMonths({
  start,
  end,
  occupiedRanges,
  leftMonthCursor,
  onLeftMonthCursorChange,
  onChangeRange,
}) {
  const rightCursor = useMemo(
    () => new Date(leftMonthCursor.getFullYear(), leftMonthCursor.getMonth() + 1, 1),
    [leftMonthCursor]
  );

  const leftDays = useMemo(() => getCalendarGrid(leftMonthCursor), [leftMonthCursor]);
  const rightDays = useMemo(() => getCalendarGrid(rightCursor), [rightCursor]);
  const range = useMemo(() => clampRange(start, end), [start, end]);

  const pick = (ymd) => {
    if (getOccupiedDay(ymd, occupiedRanges)) return;

    if (!start || (start && end)) {
      onChangeRange(ymd, "");
      return;
    }

    const next = clampRange(start, ymd);
    if (rangeContainsOccupied(next.start, next.end, occupiedRanges)) {
      onChangeRange(next.start, next.end, "El rango seleccionado se superpone con un cierre existente.");
      return;
    }

    onChangeRange(next.start, next.end, "");
  };

  const renderMonth = (cursor, days) => {
    const monthLabel = cursor.toLocaleDateString("es-AR", { month: "short" }).replace(".", "");
    const yearLabel = String(cursor.getFullYear());

    return (
      <Box sx={{ flex: 1, minWidth: 280 }}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "center", gap: 2, mb: 1 }}>
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900", textTransform: "lowercase" }}>
              {monthLabel}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "primary.700" }}>▼</Typography>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {yearLabel}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "primary.700" }}>▼</Typography>
          </Stack>
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
          {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day) => (
            <Typography key={day} sx={{ fontSize: 10.5, color: "primary.600", textAlign: "center" }}>
              {day}
            </Typography>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
          {days.map((day) => {
            const inMonth = day.type === "current";
            const selectedStart = day.ymd === range.start;
            const selectedEnd = day.ymd === range.end;
            const inRange = isBetween(day.ymd, range.start, range.end);
            const occupied = getOccupiedDay(day.ymd, occupiedRanges);
            const isAnchor = selectedStart || selectedEnd;
            const occupiedColor =
              occupied?.status === "DRAFT"
                ? "rgba(255, 224, 130, 0.92)"
                : "rgba(185, 246, 202, 0.88)";

            return (
              <Box
                key={day.ymd}
                onClick={() => pick(day.ymd)}
                title={occupied ? "Día ocupado por un cierre existente" : ""}
                sx={{
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: isAnchor ? "8px" : "6px",
                  bgcolor: isAnchor
                    ? "#1976d2"
                    : occupied
                      ? occupiedColor
                      : inRange
                        ? "rgba(25, 118, 210, 0.22)"
                        : "rgba(0,0,0,0.04)",
                  color: isAnchor
                    ? "white"
                    : occupied
                      ? "primary.900"
                      : inMonth
                        ? "primary.900"
                        : "primary.400",
                  fontSize: 12.5,
                  fontWeight: isAnchor || occupied ? 800 : 500,
                  cursor: occupied ? "not-allowed" : "pointer",
                  userSelect: "none",
                  opacity: inMonth ? 1 : 0.52,
                  border: occupied ? "1px solid rgba(15,23,42,0.08)" : "1px solid transparent",
                  "&:hover": occupied
                    ? {}
                    : {
                        bgcolor: isAnchor
                          ? "#1565c0"
                          : inRange
                            ? "rgba(25, 118, 210, 0.30)"
                            : "rgba(25, 118, 210, 0.12)",
                      },
                }}
              >
                {day.label}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "12px",
        p: 2,
        boxShadow: "0px 0px 0px 1px rgba(0,0,0,0.08)",
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <IconButton
          onClick={() =>
            onLeftMonthCursorChange((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
          }
          size="small"
        >
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.700" }}>‹</Typography>
        </IconButton>

        <Box sx={{ flex: 1 }} />

        <IconButton
          onClick={() =>
            onLeftMonthCursorChange((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
          }
          size="small"
        >
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.700" }}>›</Typography>
        </IconButton>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 3, alignItems: "flex-start" }}>
        {renderMonth(leftMonthCursor, leftDays)}
        {renderMonth(rightCursor, rightDays)}
      </Stack>
    </Box>
  );
}

export default function TesoreriaCrearCierrePage() {
  const navigate = useNavigate();

  const [occupiedRanges, setOccupiedRanges] = useState([]);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [shortcut, setShortcut] = useState("days7");
  const [leftMonthCursor, setLeftMonthCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [loadingRanges, setLoadingRanges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");

  const nextAvailableStart = useMemo(
    () => getNextAvailableStart(occupiedRanges),
    [occupiedRanges]
  );

  const moveCalendarToRange = (startYmd) => {
    const date = ymdToDate(startYmd);
    if (!date) return;
    setLeftMonthCursor(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  const applyShortcut = (key, startOverride = nextAvailableStart) => {
    const baseStart = startOverride || fmtYMD(new Date());
    let nextStart = baseStart;
    let nextEnd = addDaysYMD(baseStart, 6);

    if (key === "days10") nextEnd = addDaysYMD(baseStart, 9);
    if (key === "days30") nextEnd = addDaysYMD(baseStart, 29);
    if (key === "month") {
      const monthRange = getMonthRangeFrom(baseStart);
      nextStart = monthRange.start;
      nextEnd = monthRange.end;
    }

    setPeriodStart(nextStart);
    setPeriodEnd(nextEnd);
    setFieldError("");
    moveCalendarToRange(nextStart);

    if (rangeContainsOccupied(nextStart, nextEnd, occupiedRanges)) {
      setFieldError("El rango seleccionado se superpone con un cierre existente.");
    }
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingRanges(true);
      setError("");

      try {
        const ranges = await listTreasuryOccupiedRanges();
        if (!alive) return;

        setOccupiedRanges(ranges);
        const nextStart = getNextAvailableStart(ranges);
        const nextEnd = addDaysYMD(nextStart, 6);
        setPeriodStart(nextStart);
        setPeriodEnd(nextEnd);
        moveCalendarToRange(nextStart);
      } catch (err) {
        if (!alive) return;
        setError(errorToMessage(err));
      } finally {
        if (alive) setLoadingRanges(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const validate = () => {
    if (!periodStart || !periodEnd) {
      return "Seleccioná fecha inicial y fecha final.";
    }

    if (periodStart === periodEnd) {
      return "El cierre debe abarcar al menos dos días.";
    }

    const range = clampRange(periodStart, periodEnd);
    if (range.start !== periodStart || range.end !== periodEnd) {
      return "La fecha final debe ser posterior a la fecha inicial.";
    }

    if (rangeContainsOccupied(periodStart, periodEnd, occupiedRanges)) {
      return "El rango seleccionado se superpone con un cierre existente.";
    }

    return "";
  };

  const handleChangeRange = (start, end, message = "") => {
    setPeriodStart(start);
    setPeriodEnd(end);
    setShortcut("custom");
    setFieldError(message);
    setError("");
  };

  const handleSubmit = async () => {
    const validationError = validate();
    setFieldError(validationError);
    if (validationError) return;

    setSubmitting(true);
    setError("");

    try {
      const closure = await createTreasuryPeriod({
        periodStart,
        periodEnd,
        name: formatClosureName(periodStart, periodEnd),
      });

      const context = {
        closureId: closure.id,
        status: "DRAFT",
        mode: "edit",
        periodStart: closure.periodStart || periodStart,
        periodEnd: closure.periodEnd || periodEnd,
      };

      try {
        sessionStorage.setItem(CURRENT_CLOSURE_KEY, JSON.stringify(context));
      } catch {
        // Si falla storage, la navegación sigue funcionando sin romper el flujo.
      }

      navigate(`/tesoreria/lienzo/${closure.id}?tab=movements`);
    } catch (err) {
      setError(errorToMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedName = periodStart && periodEnd ? formatClosureName(periodStart, periodEnd) : "";
  const submitDisabled = loadingRanges || submitting || Boolean(fieldError) || !periodStart || !periodEnd;

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <IconButton
            onClick={() => navigate("/tesoreria/lienzo")}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "secondary.500",
              "&:hover": { bgcolor: "secondary.600" },
            }}
          >
            <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
          </IconButton>

          <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
            <Pill>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
                Configurar cierre
              </Typography>
            </Pill>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: "14px" }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              px: 2.5,
              py: 1.05,
              mb: 2,
              borderRadius: "999px",
              border: "1px solid rgba(0,0,0,0.15)",
              bgcolor: "white",
              minHeight: 36,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900", mr: 1 }}>
              Nombre generado:
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.700" }}>
              {selectedName || "Seleccioná un rango"}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 3, alignItems: "flex-start" }}>
            <Box sx={{ width: { xs: "100%", md: 260 } }}>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: "primary.900", mb: 2 }}>
                Seleccionar fecha
              </Typography>

              <RadioGroup
                value={shortcut}
                onChange={(event) => {
                  const value = event.target.value;
                  setShortcut(value);
                  applyShortcut(value);
                }}
                sx={{ gap: 1.5 }}
              >
                {[
                  ["days7", "7 días"],
                  ["days10", "10 días"],
                  ["days30", "30 días"],
                  ["month", "Último mes"],
                ].map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio disabled={loadingRanges} />}
                    label={
                      <Typography sx={{ fontSize: 15, color: "#1976d2", fontWeight: 600 }}>
                        {label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>

              <Stack spacing={1.2} sx={{ mt: 4 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 34, height: 16, borderRadius: "6px", bgcolor: "rgba(185, 246, 202, 0.88)" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.700" }}>
                    Cierres completados
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 34, height: 16, borderRadius: "6px", bgcolor: "rgba(255, 224, 130, 0.92)" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.700" }}>
                    Cierres en trabajo
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                bgcolor: "rgba(0,0,0,0.08)",
                alignSelf: "stretch",
              }}
            />

            <Box sx={{ flex: 1, width: "100%" }}>
              {loadingRanges ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 8, bgcolor: "white", borderRadius: "12px" }}>
                  <CircularProgress size={34} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                    Cargando calendario...
                  </Typography>
                </Stack>
              ) : (
                <CalendarRangeTwoMonths
                  start={periodStart}
                  end={periodEnd}
                  occupiedRanges={occupiedRanges}
                  leftMonthCursor={leftMonthCursor}
                  onLeftMonthCursorChange={setLeftMonthCursor}
                  onChangeRange={handleChangeRange}
                />
              )}

              {fieldError ? (
                <Typography sx={{ mt: 1.5, fontSize: 12.5, color: "error.main", fontWeight: 700 }}>
                  {fieldError}
                </Typography>
              ) : null}

              <Typography sx={{ mt: 1, fontSize: 12, color: "primary.700" }}>
                Rango seleccionado: {periodStart ? fmtDMY(ymdToDate(periodStart)) : "—"} →{" "}
                {periodEnd ? fmtDMY(ymdToDate(periodEnd)) : "—"}
              </Typography>

              <Stack direction="row" sx={{ justifyContent: "flex-end", mt: 5 }}>
                <Button
                  onClick={handleSubmit}
                  disabled={submitDisabled}
                  sx={{
                    bgcolor: "#b9f6ca",
                    color: "primary.900",
                    px: 2.5,
                    py: 1,
                    minWidth: 118,
                    height: 46,
                    borderRadius: "24px",
                    border: "2px solid",
                    borderColor: "primary.900",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 15,
                    "&:hover": { bgcolor: "#a5f5bd" },
                    "&:disabled": { opacity: 0.55 },
                  }}
                >
                  {submitting ? <CircularProgress size={22} color="inherit" /> : "Continuar"}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
